import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';

import { Button, Modal } from '@getro/rombo';

import { trackException } from 'helpers/errorTracker';
import { trackEvent } from 'helpers/analytics';

import { Text, Box, Flex } from 'rebass/styled-components';
import { activeNetworkSelector } from '../../../redux/selectors';
import { GenericError } from '../../../components/molecules/genericError';
import { useSignOut } from '../../../hooks/useSignOut';

const DeleteAccount = () => {
  const { signOut } = useSignOut();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const activeNetwork = useSelector(activeNetworkSelector);
  const { name: networkName } = activeNetwork ?? {};
  const [isModalOpen, setIsModalOpen] = useState(false);

  const cancelTokenSource = useRef(axios.CancelToken.source());
  const deleteAccountButton = (
    <Button size="small" variant="danger">
      Leave the network
    </Button>
  );

  const deleteAccount = () => {
    setSubmitting(true);
    setError(false);
    try {
      trackEvent('delete_account');
      signOut();
    } catch (e) {
      trackException(e);
      setSubmitting(false);
      setError(true);
    }
  };

  useEffect(
    () => () => {
      cancelTokenSource.current.cancel();
    },
    [],
  );

  return (
    <Flex sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
      <Text fontSize="14px" fontWeight="medium" color="text.dark">
        Leave network
      </Text>
      <Text fontSize="14px">
        Your profile and personal information will be removed from the {networkName} network. You will no longer receive
        notifications from us.
      </Text>
      <Button size="small" variant="danger" onClick={() => setIsModalOpen(true)}>
        Leave the network
      </Button>

      {isModalOpen && (
        <Modal
          title="Are you sure you want to leave the network?"
          trigger={deleteAccountButton}
          onCancel={() => setIsModalOpen(false)}
          open={isModalOpen}
          actions={
            <Flex sx={{ gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                No, I want to stay stay part of the network
              </Button>
              <Button variant="danger" onClick={deleteAccount} loading={submitting}>
                Yes, I want to leave the network
              </Button>
            </Flex>
          }
        >
          <Text mb={3}>We&apos;re sorry you&apos;re thinking of leaving us.</Text>
          <Text mb={3}>
            By leaving this network, your profile and personal information will be removed and you will no longer
            receive notifications from us.
          </Text>
          <Text>
            If you want to rejoin in the future, you can sign up as a new user. We&apos;d love to have you back!
          </Text>
        </Modal>
      )}

      {error && <Box as={GenericError} mt={3} />}
    </Flex>
  );
};

export default DeleteAccount;
